import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import DisplayAPIsException from "../../BasicComponents/DisplayAPIsExceptionAlert";
import { logEvent, logException } from "../../helper/AppInsightsHelper";
import { CustomShimmer } from "../../helper/CustomShimmer";
import { useGetTenantSupportDetailsQuery } from "../../slices/ExploreCustomers.slice";
import SupportChart from "./SupportCharts";
import SupportTable from "./SupportTable";

const SupportContainer = (props: any) => {
  // Properties
  const [searchParams] = useSearchParams();
  const tenantId = searchParams.get("id");
  const [filteredItems, setFilteredItems] = useState([]);

  // Change: chartFilters as array to handle multiple filters
  const [chartFilters, setChartFilters] = useState<
    Array<{
      filterKey: string;
      value: string;
    }>
  >([]);

  const { data: supportDataResponse, error, isFetching } = useGetTenantSupportDetailsQuery(tenantId);

  // Events
  const HandleAPIExcpetion = () => (error && <DisplayAPIsException error={error} height={250} />) || "";

  const customProps = {
    tenantId: tenantId,
    tenantName: props?.tenantName
  };

  // Handler to manage array of filters
  const handleChartFilter = (newFilter: { filterKey: string; value: string }) => {
    setChartFilters((prevFilters) => {
      // If filterKey or value is empty, reset all filters
      if (!newFilter.filterKey || !newFilter.value) {
        return [];
      }

      // Check if filter for this key already exists
      const existingFilterIndex = prevFilters.findIndex((f) => f.filterKey === newFilter.filterKey);

      if (existingFilterIndex === -1) {
        // Add new filter
        return [...prevFilters, newFilter];
      } else {
        // Update existing filter
        const updatedFilters = [...prevFilters];
        updatedFilters[existingFilterIndex] = newFilter;
        return updatedFilters;
      }
    });
  };

  // Hooks
  useEffect(() => {
    logEvent("Support", "Customer Page", "SupportContainer", customProps);
  }, []);

  useEffect(() => {
    setFilteredItems(supportDataResponse?.data ?? []);

    if (error) {
      logException(
        "Customer Page - Support Tab",
        "SupportContainer",
        "useGetTenantSupportDetailsQuery",
        error,
        customProps
      );
    }
  }, [supportDataResponse, error]);

  useEffect(() => {
    const filteredData = (supportDataResponse?.data || []).filter(
      ({ caseAgeBucket, productName, closedMonthYear, serviceRequestState }: any) => {
        // If no filters, show all data
        if (chartFilters.length === 0) {
          return true;
        }

        // Apply all filters - item must match ALL active filters
        return chartFilters.every((filter) => {
          if (filter.filterKey === "closedMonthYear") {
            return closedMonthYear === filter.value;
          } else if (filter.filterKey === "caseAgeBucket") {
            return caseAgeBucket === filter.value;
          } else if (filter.filterKey === "productName") {
            return productName === filter.value;
          } else if (filter.filterKey === "serviceRequestState") {
            return serviceRequestState === filter.value;
          }
          return true;
        });
      }
    );

    setFilteredItems(filteredData);
  }, [chartFilters, supportDataResponse]);

  return (
    <>
      <SupportChart
        error={error}
        loading={isFetching}
        onSelectedChartFilter={handleChartFilter}
        tenantId={tenantId}
        tenantName={props.tenantName}
        options={{
          filteredData: filteredItems,
          originalData: supportDataResponse?.data || [],
          chartOptions: [
            {
              id: 1,
              field: "closedMonthYear",
              key: "customer-view-customer-engagements-support-chart-by-closedMonthYear",
              title: "Volume by Closed Date",
              type: "line",
              kustoLink: supportDataResponse?.kustoLinks?.ClosedMonthYear,
              legendName: "Request Volume"
            },
            {
              id: 2,
              field: "serviceRequestState",
              key: "customer-view-customer-engagements-support-chart-by-serviceRequestState",
              title: "Case Distribution by Service Request State",
              type: "verticalbar",
              kustoLink: supportDataResponse?.kustoLinks?.ServiceRequestState
            },
            {
              id: 3,
              field: "caseAgeBucket",
              key: "customer-view-customer-engagements-support-chart-by-CaseAgeBucket",
              title: "Case Distribution by Age Bucket",
              type: "donut",
              kustoLink: supportDataResponse?.kustoLinks?.CaseAgeBucket
            },
            {
              id: 4,
              field: "productName",
              key: "customer-view-customer-engagements-support-chart-by-productName",
              title: "Volume by Product",
              type: "donut",
              kustoLink: supportDataResponse?.kustoLinks?.ProductName
            }
          ]
        }}
      />
      {isFetching ? (
        <CustomShimmer columnCount={8} />
      ) : (
        (!error && (
          <div>
            <SupportTable
              tableData={filteredItems}
              tenantName={props.tenantName}
              kustoLink={supportDataResponse?.kustoLinks?.supportDataKustoLink}
            />
          </div>
        )) ||
        ""
      )}
      <HandleAPIExcpetion />
    </>
  );
};

export default SupportContainer;
